import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import { AOPMaltaInvoiceData, AOPMaltaInvoiceDEData, AOPMaltaInvoiceFRData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPDegreeSelect from "@/src/components/AdmissionsOfficePage/shared/AOPDegreeSelect/AOPDegreeSelect";
import AOPIdSourceRow, { AOP_ID_SOURCE_DEFAULT_VALUES } from "@/src/components/AdmissionsOfficePage/shared/AOPIdSourceRow";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill, type AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAOPIdSourceFields } from "@/src/hooks/useAOPIdSourceFields";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import { useSyncWithQRPreview } from "@/src/hooks/useSyncWithQRPreview";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import {
  getInvoiceDoubleLangRegistry,
  type InvoiceDoubleLangCountry,
  type InvoiceSecondLangCode,
} from "../invoiceLetterDoubleLangRegistry";
import {
  getSecondLangFieldLabel,
  pickAdmissionSecondLangFields,
  pickInvoiceSecondLangBody,
} from "../../AdmissionLetterDoubleLang/admissionLetterDoubleLangRegistry";
import { joinSelectedCourse, splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";
import { MALTA_UZB_RUSSIAN_SIGNATURE } from "@/src/constants/AdmissionOffice/Malta/AOMaltaUzbConsts";
interface IAOPFormValues {
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  nationalitySecondLanguage: string;
  selectedCourseSecondLanguage: string;
  selectedCourse: string;
  degree?: string;
  mediumOfInstruction: string;
  mediumOfInstructionSecondLanguage: string;
  duration: string;
  durationSecondLanguage: string;
  invoiceBody: string;
  signature: string;
  task_id?: string;
  student_id?: string;
  fieldEmail?: boolean;
  userEmail?: string;
  withQR?: boolean;
}

interface AOPConfigProps {
  country: InvoiceDoubleLangCountry;
  secondLang: InvoiceSecondLangCode;
  dataForGeneration: AOPMaltaInvoiceDEData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceDEData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceDEData>>;
  setWithQRForPreview: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedSignatureForPreview: React.Dispatch<React.SetStateAction<string>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({
  country,
  secondLang,
  dataForGeneration,
  setDataForGeneration,
  setBodyForPrev,
  setWithQRForPreview,
  setSelectedSignatureForPreview,
}) => {
  const registry = getInvoiceDoubleLangRegistry(country, secondLang);
  const isUzb = secondLang === "uzb";
  const secondLangLabel = getSecondLangFieldLabel(secondLang);
  const defaultSignature = isUzb ? MALTA_UZB_RUSSIAN_SIGNATURE : "Fedorova Anzhelika";
  const secondLangInputClasses = {
    nationality: secondLang === "fr" ? "nationality-french" : "nationality-german",
    selectedCourse: secondLang === "fr" ? "selected-сourse-french" : "selected-сourse-german",
    medium: secondLang === "fr" ? "medium-of-instruction-french" : "medium-of-instruction-german",
    duration: secondLang === "fr" ? "duration-french" : "duration-german",
  };
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      invoiceBody: "",
      currentDate: formatDateForInput(today),
      withQR: true,
      signature: defaultSignature,
      ...AOP_ID_SOURCE_DEFAULT_VALUES,
    },
  });

  const dispatch = useDispatch();
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [invoiceContentSecondLanguage, setInvoiceContentSecondLanguage] = useState<string>("");
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const applyIdSourceLockRef = useRef<(payload: AdmissionTaskPrefillPayload) => void>(() => {});
  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    setValue: (name, value) => setValue(name as keyof IAOPFormValues, value as never),
    studentIdFormField: "student_id",
    documentType: "invoice",
  });

  const course = watch("course");
  const invoiceBody = watch("invoiceBody");
  const withQR = watch("withQR");
  const fieldEmail = watch("fieldEmail");
  const userEmail = watch("userEmail");
  const signature = watch("signature");

  useSyncWithQRPreview(withQR, setWithQRForPreview);

  useEffect(() => {
    setSelectedSignatureForPreview(signature || defaultSignature);
  }, [signature, defaultSignature, setSelectedSignatureForPreview]);

  const signatureAuto = useAutocomplete(Object.keys(registry.signatures));
  const offeredCourseAuto = useAutocomplete(Object.keys(registry.admissionTemplates));
  const invoiceBodyAuto = useAutocomplete(Object.keys(registry.invoiceTemplates));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "invoice",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto);
      applyIdSourceLockRef.current(payload);
    },
  });

  const idSource = useAOPIdSourceFields<IAOPFormValues>({
    setValue,
    getValues,
    applyTaskPrefill,
    lastPrefilledTaskIdRef,
    fieldEmail: !!fieldEmail,
    userEmail,
  });
  applyIdSourceLockRef.current = idSource.applyIdSourceLockFromPrefill;

  useEffect(() => {
    if (!course) return;
    const template = registry.admissionTemplates[course];
    if (!template) return;
    const { degree, selectedCourse } = splitSelectedCourse(template.selected_course);
    setValue("degree", degree);
    setValue("selectedCourse", selectedCourse);
    setValue("mediumOfInstruction", template.medium_of_instruction);
    setValue("duration", template.duration);

    if (!isUzb) {
      const secondValues = pickAdmissionSecondLangFields(secondLang, template);
      setValue("selectedCourseSecondLanguage", secondValues.course);
      setValue("mediumOfInstructionSecondLanguage", secondValues.medium);
      setValue("durationSecondLanguage", secondValues.duration);
    } else {
      setValue("selectedCourseSecondLanguage", "");
      setValue("mediumOfInstructionSecondLanguage", "");
      setValue("durationSecondLanguage", "");
    }
  }, [course, isUzb, registry.admissionTemplates, secondLang, setValue]);

  useEffect(() => {
    if (!invoiceBody) {
      setInvoiceContent("");
      setInvoiceContentSecondLanguage("");
      return;
    }
    const template = registry.invoiceTemplates[invoiceBody];
    if (!template) {
      setInvoiceContent("");
      setInvoiceContentSecondLanguage("");
      return;
    }
    setInvoiceContent(template.tinyMCEContent);
    setInvoiceContentSecondLanguage(isUzb ? "" : pickInvoiceSecondLangBody(secondLang, template));
  }, [invoiceBody, isUzb, registry.invoiceTemplates, secondLang]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;
      let response;
      if (secondLang === "fr") {
        response = await admissionOfficeRequests.createInvoiceDocFrench(dataForGeneration as unknown as AOPMaltaInvoiceFRData);
      } else if (secondLang === "de") {
        response = await admissionOfficeRequests.createInvoiceDocGerman(dataForGeneration);
      } else {
        const uzbPayload: AOPMaltaInvoiceData = {
          currentDate: dataForGeneration.currentDate,
          fullName: dataForGeneration.fullName,
          dateOfBirth: dataForGeneration.dateOfBirth,
          nationality: dataForGeneration.nationality,
          course: dataForGeneration.course,
          userOffice: dataForGeneration.userOffice,
          whose_doc: dataForGeneration.whose_doc,
          need_qr: dataForGeneration.need_qr,
          title: dataForGeneration.title,
          duration: dataForGeneration.duration,
          mediumOfInstruction: dataForGeneration.mediumOfInstruction,
          coursePrefix: dataForGeneration.coursePrefix,
          invoiceBody: dataForGeneration.invoiceBody,
        };
        response = await admissionOfficeRequests.createInvoiceDocUzb(uzbPayload);
      }
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const parseDate = (value: string) => {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    };

    const watchedFields = {
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      dateOfBirth: watch("dateOfBirth"),
      nationality: watch("nationality"),
      selectedCourse: watch("selectedCourse"),
      degree: watch("degree"),
      whose_doc: watch("whose_doc"),
      duration: watch("duration"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      coursePrefix: watch("coursePrefix"),
      nationalitySecondLanguage: watch("nationalitySecondLanguage"),
      selectedCourseSecondLanguage: watch("selectedCourseSecondLanguage"),
      mediumOfInstructionSecondLanguage: watch("mediumOfInstructionSecondLanguage"),
      durationSecondLanguage: watch("durationSecondLanguage"),
      withQR: watch("withQR"),
    };
    const joinedSelectedCourse = joinSelectedCourse(watchedFields.degree, watchedFields.selectedCourse);
    const sharedFields = {
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      course: joinedSelectedCourse || "",
      whose_doc: watchedFields.whose_doc || "",
      duration: watchedFields.duration || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      coursePrefix: watchedFields.coursePrefix || "",
      invoiceBody: splitHtmlByBlocks(invoiceContent),
      invoiceBodySecondLanguage: isUzb ? [] : splitHtmlByBlocks(invoiceContentSecondLanguage),
      nationalitySecondLanguage: isUzb ? "" : watchedFields.nationalitySecondLanguage || "",
      courseSecondLanguage: isUzb ? "" : watchedFields.selectedCourseSecondLanguage || "",
      mediumOfInstructionSecondLanguage: isUzb ? "" : watchedFields.mediumOfInstructionSecondLanguage || "",
      durationSecondLanguage: isUzb ? "" : watchedFields.durationSecondLanguage || "",
    };

    setDataForGeneration((prev) => ({
      ...prev,
      ...sharedFields,
      need_qr: !!watchedFields.withQR,
    }));

    setBodyForPrev((prev) => ({
      ...prev,
      ...sharedFields,
      invoiceBody: splitHtmlByBlocksWithFontConvert(invoiceContent),
      invoiceBodySecondLanguage: isUzb ? [] : splitHtmlByBlocksWithFontConvert(invoiceContentSecondLanguage),
    }));

    if (invoiceContent.trim().length > 0 || (!isUzb && invoiceContentSecondLanguage.trim().length > 0)) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleCopy = async (fieldName: keyof IAOPFormValues) => {
    const value = getValues(fieldName);
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <div className="admission-office-page-config">
      <div className="admission-office-page-config-logo">
        <div className="logo-svg">
          <LogoSvg />
        </div>
        <div className="logo-line"></div>
        <p>Platforma</p>
      </div>
      <p className="admission-office-page-config-title">{isUzb ? "INVOICE UZB LETTER" : "INVOICE DOUBLE LANGUAGE LETTER"}</p>
      <form className="admission-office-page-config-form">
        <AOPIdSourceRow
          control={control}
          register={register}
          setError={setError}
          clearErrors={clearErrors}
          applyTaskPrefill={idSource.applyTaskPrefill}
          lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
          lastPrefilledStudentIdRef={idSource.lastPrefilledStudentIdRef}
          fieldEmail={!!fieldEmail}
          isTaskIdDisabled={idSource.isTaskIdDisabled}
          isStudentIdDisabled={idSource.isStudentIdDisabled}
          isFieldEmailDisabled={idSource.isFieldEmailDisabled}
          onClearTaskId={idSource.handleClearTaskId}
          onClearStudentId={idSource.handleClearStudentId}
          onClearUserEmail={idSource.handleClearUserEmail}
          onCheckUserEmail={() => void idSource.handleCheckUserEmail()}
          onCopy={idSource.handleCopyIdField}
        />
        <div className="form-group-fields">
          <div className="form-group-fields-container">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.invoiceBody}
                  inputProps={{
                  register: register("whose_doc"),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                  onArrowClick: whoseDocAuto.onArrowClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input coursePrefix">
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
            </div>
          </div>
          <div className="form-group-fields-input currentDate">
            <div className="form-group-fields-input-label">
              <label htmlFor="currentDate">
                Date of Issue
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="full-mame">
                Full Name<span>*</span>
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
            <div className="form-group-fields-input-svg">
              <div className="form-group-fields-input-svg-line"></div>
              <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                <AODSvgExport.FullNameCopySvg />
              </div>
            </div>
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="dateOfBirth">
                Data of Birth
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
          </div>
          <AutocompleteInput
            label="Nationality"
            value={nationalitiesAuto.query}
            onChange={nationalitiesAuto.onChange}
            onSelect={(v) => {
              nationalitiesAuto.onSelect(v);
              setValue("nationality", v);
            }}
            isOpen={nationalitiesAuto.isOpen}
            options={nationalitiesAuto.filtered}
            error={!!errors.nationality}
              inputProps={{
              register: register("nationality"),
              close: nationalitiesAuto.close,
              setOpen: nationalitiesAuto.onClick,
              onArrowClick: nationalitiesAuto.onArrowClick,
            }}
            identifier={"nationality"}
          />
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input course">
            <AutocompleteInput
              label="Select the offered course"
              value={offeredCourseAuto.query}
              onChange={offeredCourseAuto.onChange}
              onSelect={(v) => {
                offeredCourseAuto.onSelect(v);
                setValue("course", v);
              }}
              isOpen={offeredCourseAuto.isOpen}
              options={offeredCourseAuto.filtered}
              error={!!errors.course}
                inputProps={{
                register: register("course"),
                close: offeredCourseAuto.close,
                setOpen: offeredCourseAuto.onClick,
                onArrowClick: offeredCourseAuto.onArrowClick,
                setValue,
                clearErrors,
                name: "course",
              }}
              identifier={"offered-course-auto"}
            />
          </div>
        </div>

        {!isUzb ? (
          <>
            <div className="form-group-fields">
              <div className="form-group-fields-input">
                <div className="form-group-fields-input-label">
                  <label htmlFor="nationalitySecondLanguage">Nationality ({secondLangLabel})</label>
                  <div className="form-group-fields-input-label-line"></div>
                </div>
                <input
                  className={secondLangInputClasses.nationality}
                  id="nationalitySecondLanguage"
                  type="text"
                  {...register("nationalitySecondLanguage")}
                />
              </div>
            </div>
            <div className="form-group-fields">
              <div className="form-group-fields-input">
                <div className="form-group-fields-input-label">
                  <label htmlFor="selectedCourseSecondLanguage">Selected course ({secondLangLabel})</label>
                  <div className="form-group-fields-input-label-line"></div>
                </div>
                <input
                  className={secondLangInputClasses.selectedCourse}
                  id="selectedCourseSecondLanguage"
                  type="text"
                  {...register("selectedCourseSecondLanguage")}
                />
              </div>
            </div>
          </>
        ) : null}
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="degree">Degree</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <AOPDegreeSelect control={control} />
          </div>
        </div>
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="selectedCourse">Selected course</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="selected-сourse" id="selectedCourse" type="text" {...register("selectedCourse")} />
          </div>
        </div>
        {!isUzb ? (
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="mediumOfInstructionSecondLanguage">Medium ({secondLangLabel})</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className={secondLangInputClasses.medium}
                id="mediumOfInstructionSecondLanguage"
                type="text"
                {...register("mediumOfInstructionSecondLanguage")}
              />
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="durationSecondLanguage">Duration ({secondLangLabel})</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className={secondLangInputClasses.duration}
                id="durationSecondLanguage"
                type="text"
                {...register("durationSecondLanguage")}
              />
            </div>
          </div>
        ) : null}
        <div className="form-group-fields">
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="mediumOfInstruction">Medium of Instruction</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="medium-of-instruction"
              id="mediumOfInstruction"
              type="text"
              {...register("mediumOfInstruction")}
            />
          </div>
          <div className="form-group-fields-input">
            <div className="form-group-fields-input-label">
              <label htmlFor="duration">Duration</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input className="duration" id="duration" type="text" {...register("duration")} />
          </div>
        </div>

        <div className="form-group-fields">
          <div className="form-group-fields-input invoiceBody">
            <AutocompleteInput
              label="Invoice details"
              value={invoiceBodyAuto.query}
              onChange={invoiceBodyAuto.onChange}
              onSelect={(v) => {
                invoiceBodyAuto.onSelect(v);
                setValue("invoiceBody", v);
              }}
              isOpen={invoiceBodyAuto.isOpen}
              options={invoiceBodyAuto.filtered}
              error={!!errors.invoiceBody}
                inputProps={{
                register: register("invoiceBody"),
                close: invoiceBodyAuto.close,
                setOpen: invoiceBodyAuto.onClick,
                onArrowClick: invoiceBodyAuto.onArrowClick,
              }}
              identifier={"invoice"}
            />
          </div>
        </div>

        {!isUzb ? (
          <>
            <label>Invoice body second language</label>
            <Editor
              value={invoiceContentSecondLanguage}
              init={{
                height: TINYMCE_EDITOR_HEIGHT,
                menubar: false,
                plugins: ["lists", "link", "code", "table"],
                toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                branding: false,
                toolbar_mode: "wrap",
                licenseKey: "gpl",
                license_key: "gpl" as any,
                content_style: TINYMCE_EDITOR_CONTENT_STYLE,
              }}
              onEditorChange={setInvoiceContentSecondLanguage}
              tinymceScriptSrc="/tinymce/tinymce.min.js"
            />
          </>
        ) : null}
        <label>Invoice body</label>
        <Editor
          value={invoiceContent}
          init={{
            height: TINYMCE_EDITOR_HEIGHT,
            menubar: false,
            plugins: ["lists", "link", "code", "table"],
            toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
            branding: false,
            toolbar_mode: "wrap",
            licenseKey: "gpl",
            license_key: "gpl" as any,
            content_style: TINYMCE_EDITOR_CONTENT_STYLE,
          }}
          onEditorChange={setInvoiceContent}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
        />

        <AOPGeneratedDocumentToolbar
          isGenerated={generatedDoc.isGenerated}
          generatedPdfUrl={generatedDoc.generatedPdfUrl}
          taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
          previewPdfUrl={generatedDoc.previewPdfUrl}
          canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
          canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
          isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
          setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
          generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
          onDownload={generatedDoc.handleDownloadGeneratedPdf}
          onApprove={generatedDoc.handlePreviewApprove}
          onReject={generatedDoc.handlePreviewReject}
          isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
          previewButton={
            <BtnDefault onClick={handlePreview} reverse={true}>
              <p>Preview doc</p>
              <AODSvgExport.PreviewSvg />
            </BtnDefault>
          }
          generateButton={
            <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
              <p>Generate doc</p>
              <AODSvgExport.GenerateSvg />
            </BtnDefault>
          }
        />
        <div className="form-group-fields form-group-fields-signature-row">
          <div className="form-group-fields-input signature">
            <AutocompleteInput
              label="Signature"
              value={signatureAuto.query}
              defaultValue={defaultSignature}
              onChange={signatureAuto.onChange}
              onSelect={(v) => {
                signatureAuto.onSelect(v);
                setValue("signature", v);
              }}
              isOpen={signatureAuto.isOpen}
              options={signatureAuto.filtered}
              error={!!errors.signature}
              inputProps={{
                register: register("signature"),
                close: signatureAuto.close,
                setOpen: signatureAuto.onClick,
                onArrowClick: signatureAuto.onArrowClick,
              }}
              identifier={"signature"}
            />
          </div>
          <div className="form-group-fields-invoice">
            <Controller
              name="withQR"
              control={control}
              render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AOPConfig;
